import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "..";
import {
  setDrive,
  setDriveWithValidation,
  addDrive,
  removeDrive,
  updateDriveRole,
  setPowerPlant,
  setFuelTanks,
} from "../features/shipBuilderSlice";
import { calculateFuelTankRequirements } from "../../utils/calculations/fuelCalculations";
import { POWER_PLANTS } from "../../constants/powerPlants";
import { selectCentralizedPowerRequirements } from "../../utils/calculations/centralizedPowerCalculations";

/**
 * Middleware to automatically update power plant and fuel tanks when drives are added, removed, or changed
 */
const drivePowerPlantMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    // First, let the action pass through and update the state
    const result = next(action);

    // Check if this is a drive action that affects power requirements
    if (
      setDrive.match(action) ||
      setDriveWithValidation.match(action) ||
      addDrive.match(action) ||
      removeDrive.match(action) ||
      updateDriveRole.match(action)
    ) {
      // Get the updated state after the action has been processed
      const state = store.getState().shipBuilder;
      const { drives, shipSize, techLevel, powerPlant } = state;

      // Skip calculation if state is not properly initialized
      if (!shipSize || shipSize <= 0) {
        console.log("drivePowerPlantMiddleware: Skipping calculation - ship size not initialized");
        return result;
      }

      console.log("drivePowerPlantMiddleware: Drive change detected, recalculating power plant and fuel tanks");

      // Use the exact same power calculation as the Power Plant step
      const powerRequirements = selectCentralizedPowerRequirements(store.getState());
      const totalPowerRequired = powerRequirements.normalOperations;

      console.log(`drivePowerPlantMiddleware: Total power required: ${totalPowerRequired} (normalOperations from centralized calculation)`);

      // Check if current power plant can handle the new power requirements
      const currentPowerOutput = powerPlant ? powerPlant.power : 0;

      if (currentPowerOutput < totalPowerRequired) {
        console.log(`drivePowerPlantMiddleware: Current power output (${currentPowerOutput}) insufficient, calculating new power plant`);
        
        // Use the same auto-selection logic as the Power Plant step
        // Find the highest-rated power plant available for current tech level
        const availablePowerPlants = Object.entries(POWER_PLANTS)
          .filter(([_, plantSpec]) => plantSpec.techLevel <= techLevel)
          .sort(([_, a], [__, b]) => b.techLevel - a.techLevel); // Sort by tech level descending

        let selectedType = powerPlant?.type || "fission";
        if (availablePowerPlants.length > 0) {
          const [bestPowerPlantType] = availablePowerPlants[0];
          selectedType = bestPowerPlantType as keyof typeof POWER_PLANTS;
        }
        
        const selectedSpec = POWER_PLANTS[selectedType];
        
        // Calculate power plant size using the same logic as PowerPlantSelector
        const autoPowerPlantTons = Math.round(
          Math.max(0.1, 
            totalPowerRequired / selectedSpec.powerPerTon
          ) * 100
        ) / 100; // Round to nearest 0.01
        
        const powerPlantCost = autoPowerPlantTons * selectedSpec.costPerTon;
        const powerPlantPower = autoPowerPlantTons * selectedSpec.powerPerTon;
        const maintenanceCost = autoPowerPlantTons * selectedSpec.maintenanceCost;
        
        const newPowerPlant = {
          type: selectedSpec.type,
          rating: 0,
          cost: powerPlantCost * 1000000, // Convert to credits
          weight: autoPowerPlantTons,
          power: powerPlantPower,
          techLevel: selectedSpec.techLevel,
          maintenanceCost: maintenanceCost * 1000000, // Convert to credits
          description: selectedSpec.description,
        };
        
        console.log("drivePowerPlantMiddleware: Updating power plant:", newPowerPlant);
        
        // Update power plant
        store.dispatch(setPowerPlant(newPowerPlant));

        // Update fuel tanks based on new power plant
        const fuelTankResult = calculateFuelTankRequirements({
          powerPlant: newPowerPlant,
          drives,
          shipSize,
        });

        if (fuelTankResult.success && fuelTankResult.data) {
          console.log("drivePowerPlantMiddleware: Updating fuel tanks:", fuelTankResult.data.total);
          store.dispatch(setFuelTanks(fuelTankResult.data.total));
        }
      } else {
        console.log(`drivePowerPlantMiddleware: Current power plant sufficient (${currentPowerOutput} >= ${totalPowerRequired})`);
        
        // Even if power plant is sufficient, we should still update fuel tanks
        // because drive changes can affect fuel requirements
        if (powerPlant) {
          const fuelTankResult = calculateFuelTankRequirements({
            powerPlant,
            drives,
            shipSize,
          });

          if (fuelTankResult.success && fuelTankResult.data) {
            console.log("drivePowerPlantMiddleware: Updating fuel tanks for existing power plant:", fuelTankResult.data.total);
            store.dispatch(setFuelTanks(fuelTankResult.data.total));
          }
        }
      }
    }

    return result;
  };

export default drivePowerPlantMiddleware;
